// Geteilter Zustand für Benachrichtigungen.
//
// Die Glocke und der Chat-Eintrag in der Navigation zeigen Teile derselben
// Information. Ohne gemeinsamen Zustand würde jede Stelle einzeln nachfragen —
// doppelte Last und Zähler, die kurzzeitig auseinanderlaufen.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';

const ABFRAGE_MS = 30000;

export type Nachricht = { id: string; von: string; vonId: string; anAlle: boolean; text: string; createdAt: string };
export type AufgabenHinweis = { id: string; titel: string; art: 'faellig' | 'neu'; hinweis: string; dueDate: string | null };
export type SupportHinweis = { id: string; nummer: string; betreff: string; zeit: string };

type Daten = {
  nachrichten: Nachricht[];
  aufgaben: AufgabenHinweis[];
  support?: SupportHinweis[];
  anzahl: { nachrichten: number; aufgaben: number; support?: number; gesamt: number };
};

type Ctx = {
  nachrichten: Nachricht[];
  aufgaben: AufgabenHinweis[];
  /** Antworten vom Support, die noch ungelesen sind. */
  support: SupportHinweis[];
  /** Zähler für den Chat-Eintrag in der Navigation. */
  anzahlChat: number;
  /** Zähler für die Glocke — alles außer Chat. */
  anzahlSonstige: number;
  neuLaden: () => Promise<void>;
  alsGesehenMelden: () => Promise<void>;
};

const Kontext = createContext<Ctx | null>(null);

function systemhinweis(titel: string, text: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(titel, { body: text, tag: 'lagerpro', icon: '/logo.svg' });
  } catch { /* je nach Browser-Kontext blockiert */ }
}

export function BenachrichtigungsProvider({ children }: { children: ReactNode }) {
  const { benutzer } = useAuth();
  const [daten, setDaten] = useState<Daten | null>(null);
  const vorigeGesamt = useRef(0);

  const laden = useCallback(async () => {
    try {
      const neu = await api<Daten>('/notifications');
      setDaten(neu);

      if (neu.anzahl.gesamt > vorigeGesamt.current && vorigeGesamt.current > 0) {
        const erste = neu.nachrichten[0];
        systemhinweis('LagerPro', erste ? `${erste.von}: ${erste.text}` : 'Neue Vorgänge');
      }
      vorigeGesamt.current = neu.anzahl.gesamt;
    } catch { /* stille Hintergrundabfrage */ }
  }, []);

  useEffect(() => {
    // Ohne Anmeldung gibt es nichts abzufragen. Vorher lief die Abfrage auch
    // auf der Anmeldemaske weiter und erzeugte dort alle 30 Sekunden eine 401.
    // Im Browser fällt das kaum auf, auf dem Telefon weckt es den Funk.
    if (!benutzer) { setDaten(null); vorigeGesamt.current = 0; return; }
    void laden();
    const id = setInterval(() => { void laden(); }, ABFRAGE_MS);
    return () => clearInterval(id);
  }, [laden, benutzer]);

  const alsGesehenMelden = useCallback(async () => {
    await api('/notifications/gesehen', { methode: 'POST', daten: {} }).catch(() => {});
  }, []);

  const wert = useMemo<Ctx>(() => ({
    nachrichten: daten?.nachrichten ?? [],
    aufgaben: daten?.aufgaben ?? [],
    support: daten?.support ?? [],
    anzahlChat: daten?.anzahl.nachrichten ?? 0,
    anzahlSonstige: (daten?.anzahl.aufgaben ?? 0) + (daten?.anzahl.support ?? 0),
    neuLaden: laden,
    alsGesehenMelden,
  }), [daten, laden, alsGesehenMelden]);

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useBenachrichtigungen() {
  const c = useContext(Kontext);
  // Ausserhalb des Providers (z. B. Anmeldeseite) leere Werte liefern statt
  // zu werfen — der Aufrufer soll sich nicht darum kümmern müssen.
  return c ?? {
    nachrichten: [], aufgaben: [], support: [], anzahlChat: 0, anzahlSonstige: 0,
    neuLaden: async () => {}, alsGesehenMelden: async () => {},
  };
}
